import { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import type { Organization, OrganizationMember, OrganizationInvitation, OrgRole } from '@/types';
import * as svc from '@/services';
import type { AssignableRole } from '@/services/org-members';

interface OrganizationContextType {
  organization: Organization | null;
  members: OrganizationMember[];
  invitations: OrganizationInvitation[];
  userRole: OrgRole | null;
  loading: boolean;
  hasOrganization: boolean;
  /**
   * Holds the five administrative permissions a MEMBER does not:
   * SETTINGS_MANAGE (API keys, gateway, webhooks), BILLING_MANAGE,
   * TEAM_MANAGE, ORG_MANAGE (organisation identity and the public review
   * page) and AUDIT_LOGS_VIEW. Mirrors ROLE_PERMISSIONS in
   * api/src/auth/rbac/permissions.enum.ts, where owner and admin hold every
   * permission and a member holds the day-to-day ones only.
   *
   * Use this, not `isOrgOwner`, to decide whether to SHOW something. Owner-only
   * actions (ownership transfer, deactivation) are narrowed by the service
   * against Organization.ownerId and are what `isOrgOwner` is for.
   */
  isOrgAdmin: boolean;
  isOrgOwner: boolean;
  refreshOrganization: () => Promise<void>;
  refreshMembers: () => Promise<void>;
  refreshInvitations: () => Promise<void>;
  createOrganization: (name: string, options?: { defaultSenderId?: string; timezone?: string }) => Promise<{ error: Error | null; organizationId?: string }>;
  updateOrganization: (updates: Partial<Pick<Organization, 'name' | 'billingEmail' | 'timezone' | 'unsubscribeFooterEnabled' | 'vatNumber' | 'termsUrl' | 'privacyUrl'>>) => Promise<{ error: Error | null }>;
  updateDefaultSenderId: (senderId: string, orgId?: string) => Promise<{ error: Error | null }>;
  inviteMember: (email: string, role?: AssignableRole) => Promise<{ error: Error | null }>;
  removeMember: (memberId: string) => Promise<{ error: Error | null }>;
  updateMemberRole: (memberId: string, role: AssignableRole) => Promise<{ error: Error | null }>;
  cancelInvitation: (invitationId: string) => Promise<{ error: Error | null }>;
  displayCredits: number;
  orgId: string | null;
}

/**
 * Re-exported so the many callers that reach for the role through this hook do
 * not each import from `@/types`. The definition lives beside
 * `OrganizationMember`, which is the thing that carries it.
 */
export type { OrgRole };

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// ── helpers ────────────────────────────────────────────────────────────

function normalizeOrg(orgData: any): Organization {
  return { ...orgData } as Organization;
}

async function fetchMembersWithProfiles(orgId: string): Promise<OrganizationMember[]> {
  const data = await svc.fetchOrgMembers(orgId);
  if (data.length === 0) return [];

  const profiles = await svc.fetchProfilesByIds(data.map(m => m.userId));
  const profileMap = new Map(profiles.map(p => [p.id, p]));

  return data.map(m => ({
    id: m.id,
    organizationId: m.organizationId,
    userId: m.userId,
    role: m.role as 'owner' | 'member',
    permissions: m.permissions as OrganizationMember['permissions'],
    joinedAt: m.joinedAt,
    profile: profileMap.get(m.userId),
  }));
}

/** Wraps a mutateAsync call to return the legacy { error } shape. */
async function safeCall<T>(fn: () => Promise<T>): Promise<{ error: Error | null } & (T extends { organizationId: string } ? { organizationId?: string } : {})> {
  try {
    const result = await fn();
    return { error: null, ...((result && typeof result === 'object') ? result : {}) } as any;
  } catch (error) {
    
    return { error: error as Error } as any;
  }
}

// ── Provider ───────────────────────────────────────────────────────────

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const qc = useQueryClient();

  // 1. Fetch user membership → org_id + role.
  // Only needs user.id, so don't serialize it behind authLoading (which also
  // covers the /profiles fetch) — fire as soon as the user object exists.
  const membershipQuery = useQuery({
    queryKey: ['user-membership', user?.id],
    queryFn: () => svc.fetchUserMembership(user!.id),
    enabled: !!user,
  });

  const membership = membershipQuery.data ?? null;
  const orgId = membership?.organizationId ?? null;
  const userRole = (membership?.role as OrgRole) ?? null;
  // Owner and admin hold every permission; a member holds the day-to-day ones.
  const isOrgAdmin = userRole === 'owner' || userRole === 'admin';

  // 2. Fetch organization
  const orgQuery = useQuery({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const orgData = await svc.fetchOrganization(orgId!);
      return normalizeOrg(orgData);
    },
    enabled: !!orgId,
  });

  const organization = orgQuery.data ?? null;

  // 3. Fetch members
  const membersQuery = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: () => fetchMembersWithProfiles(orgId!),
    enabled: !!orgId,
  });

  // 4. Fetch invitations
  //
  // Gated on the role, not just on having an org. `OrganizationProvider` wraps
  // the whole authenticated shell, so this query runs for every signed-in user
  // on every page, and the server now requires owner or admin to list
  // invitations. Without the gate a plain member fires it anyway, gets a 403,
  // and React Query's `retry: 1` default turns that into two failed requests
  // plus a console error per query lifecycle, every session, on every page.
  //
  // Nothing was visibly broken: the consumer reads `?? []` and the invitation
  // panel is behind `isOrgOwner`, so the member saw an absence rather than an
  // error. That is precisely why it would have gone on indefinitely, filling
  // the console of every non-owner with 403s that mean "working as intended".
  //
  // Gated on `isOrgAdmin`, which is exactly what the server's `assertOrgAdmin`
  // accepts: owner AND admin. It used to be gated on `owner` alone, on the
  // reasoning that the panel behind it was owner-only anyway. The panel is not
  // any more: an admin holds TEAM_MANAGE, so they get the invitation UI, and
  // narrowing the query to `owner` would leave them looking at an empty list of
  // invitations they are allowed to read and allowed to send.
  const canListInvitations = isOrgAdmin;
  const invitationsQuery = useQuery({
    queryKey: ['org-invitations', orgId],
    queryFn: async () => {
      const data = await svc.fetchOrgInvitations(orgId!);
      return data.map(inv => ({ ...inv, role: inv.role as OrgRole }));
    },
    enabled: !!orgId && canListInvitations,
  });

  // ── Refresh helpers (invalidate cache) ───────────────────────────────

  const refreshOrganization = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['user-membership', user?.id] });
    await qc.invalidateQueries({ queryKey: ['organization', orgId] });
  }, [qc, user?.id, orgId]);

  const refreshMembers = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['org-members', orgId] });
  }, [qc, orgId]);

  const refreshInvitations = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['org-invitations', orgId] });
  }, [qc, orgId]);

  // ── Mutations ────────────────────────────────────────────────────────

  const createOrgMutation = useMutation({
    mutationFn: async ({ name, options }: { name: string; options?: { defaultSenderId?: string; timezone?: string } }) => {
      if (!user) throw new Error('Not authenticated');
      // No slug and no id: the server derives both, and the two this used to
      // invent were a fabricated UUID the API discarded and a slug that deleted
      // every character of a Greek or Russian name. See
      // services/organizations.ts.
      const created = await svc.insertOrganization({
        name,
        ownerId: user.id,
        billingEmail: user.email,
        defaultSenderId: options?.defaultSenderId || null,
        timezone: options?.timezone || 'Europe/Nicosia',
      });
      return { organizationId: created?.id };
    },
    onSuccess: () => refreshOrganization(),
  });

  const updateOrgMutation = useMutation({
    mutationFn: async (updates: Partial<Pick<Organization, 'name' | 'billingEmail' | 'timezone' | 'unsubscribeFooterEnabled' | 'vatNumber' | 'termsUrl' | 'privacyUrl'>>) => {
      let targetOrgId = organization?.id;
      if (!targetOrgId) {
        // Fall back to a fresh fetch if the closure state hasn't caught up
        // (e.g. mid-refetch race). Avoids the misleading "No organization" toast.
        try {
          const fresh = await svc.fetchOrganization('');
          targetOrgId = (fresh as { id?: string } | null)?.id;
        } catch {
          // swallow; handled below
        }
      }
      if (!targetOrgId) throw new Error('Unable to load your organization. Refresh and try again');
      await svc.updateOrganizationDb(targetOrgId, updates);
    },
    onSuccess: () => refreshOrganization(),
  });

  const updateSenderIdMutation = useMutation({
    mutationFn: async ({ senderId, overrideOrgId }: { senderId: string; overrideOrgId?: string }) => {
      const targetOrgId = overrideOrgId || organization?.id;
      if (!targetOrgId) throw new Error('No organization');
      await svc.updateOrganizationDb(targetOrgId, { defaultSenderId: senderId.trim() || null });
    },
    onSuccess: () => refreshOrganization(),
  });

  const inviteMemberMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: AssignableRole }) => {
      if (!organization || !user) throw new Error('No organization');
      await svc.insertOrgInvitation({ organizationId: organization.id, email, invitedBy: user.id, role });
    },
    onSuccess: () => refreshInvitations(),
  });

  // PATCH /organizations/members/:id has existed since the role split and had
  // no client caller, so a role could be given at invite time and never
  // changed. The server refuses to demote the sitting owner through it;
  // ownership moves through transfer-ownership alone.
  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: AssignableRole }) => {
      await svc.updateOrgMemberRole(memberId, role);
    },
    onSuccess: () => refreshMembers(),
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      if (!organization) throw new Error('No organization');
      await svc.deleteOrgMember(memberId, organization.id);
    },
    onSuccess: () => refreshMembers(),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      if (!organization) throw new Error('No organization');
      await svc.deleteOrgInvitation(invitationId, organization.id);
    },
    onSuccess: () => refreshInvitations(),
  });

  // ── Stable wrappers (preserve public API shape) ─────────────────────

  const createOrganization = useCallback(
    (name: string, options?: { defaultSenderId?: string; timezone?: string }) =>
      safeCall(() => createOrgMutation.mutateAsync({ name, options })),
    [createOrgMutation],
  );

  const updateOrganization = useCallback(
    (updates: Partial<Pick<Organization, 'name' | 'billingEmail' | 'timezone' | 'unsubscribeFooterEnabled' | 'vatNumber' | 'termsUrl' | 'privacyUrl'>>) =>
      safeCall(() => updateOrgMutation.mutateAsync(updates)),
    [updateOrgMutation],
  );

  const updateDefaultSenderId = useCallback(
    (senderId: string, overrideOrgId?: string) =>
      safeCall(() => updateSenderIdMutation.mutateAsync({ senderId, overrideOrgId })),
    [updateSenderIdMutation],
  );

  const inviteMember = useCallback(
    (email: string, role: AssignableRole = 'member') =>
      safeCall(() => inviteMemberMutation.mutateAsync({ email, role })),
    [inviteMemberMutation],
  );

  const updateMemberRole = useCallback(
    (memberId: string, role: AssignableRole) =>
      safeCall(() => updateMemberRoleMutation.mutateAsync({ memberId, role })),
    [updateMemberRoleMutation],
  );

  const removeMember = useCallback(
    (memberId: string) =>
      safeCall(() => removeMemberMutation.mutateAsync(memberId)),
    [removeMemberMutation],
  );

  const cancelInvitation = useCallback(
    (invitationId: string) =>
      safeCall(() => cancelInvitationMutation.mutateAsync(invitationId)),
    [cancelInvitationMutation],
  );

  // ── Derived ──────────────────────────────────────────────────────────

  const loading = authLoading || membershipQuery.isLoading || (!!orgId && orgQuery.isLoading);
  const displayCredits = (organization?.monthlyBaseCredits ?? 0) + (organization?.topUpCreditsBalance ?? 0);

  const value = useMemo<OrganizationContextType>(() => ({
    organization,
    members: membersQuery.data ?? [],
    invitations: invitationsQuery.data ?? [],
    userRole,
    loading,
    hasOrganization: !!organization,
    isOrgAdmin,
    isOrgOwner: userRole === 'owner',
    refreshOrganization,
    refreshMembers,
    refreshInvitations,
    createOrganization,
    updateOrganization,
    updateDefaultSenderId,
    inviteMember,
    removeMember,
    updateMemberRole,
    cancelInvitation,
    displayCredits,
    orgId,
  }), [
    organization, orgId,
    membersQuery.data, invitationsQuery.data,
    userRole, isOrgAdmin, loading, displayCredits,
    refreshOrganization, refreshMembers, refreshInvitations,
    createOrganization, updateOrganization, updateDefaultSenderId,
    inviteMember, removeMember, updateMemberRole, cancelInvitation,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
